'use client';

import { useMemo } from 'react';
import { FacetChoice } from '@akinon/next/types';
import { useGetWidgetQuery } from '@akinon/next/data/client/misc';

/**
 * `online-special-category-filter` widget'ı, kategori facet'inin özel sayfalarda
 * nasıl davranacağını sayfa bazında yönetir:
 *
 * - `category_filter_settings`: kategori filtresi bu sayfada görünsün mü
 *   (`is_show`) ve alt kategori kırılımı açık mı (`is_subcategory_show`).
 * - `subcategory_filter_settings`: bu sayfada gösterilecek ANA kategorilerin
 *   beyaz listesi (`subcategory`, virgülle ayrılmış isimler).
 */
const CATEGORY_FILTER_WIDGET_SLUG = 'online-special-category-filter';

export const CATEGORY_FACET_KEY = 'category_ids';

/** Kullanıcıya hiç gösterilmeyen facet'ler. */
const HIDDEN_FACET_KEYS = ['in_stock'];

/** Widget dropdown'ları bazı kayıtlarda `true`, bazılarında `show` döndürüyor. */
const ENABLED_VALUES = ['true', 'show'];

const SUBCATEGORY_TREE_ENABLED_VALUE = 'show';

type SettingValue = {
  is_show?: string;
  page_url?: string;
  is_subcategory_show?: string;
  subcategory?: string;
};

type SettingItem = SettingValue & { value?: SettingValue };

type CategoryFilterWidget = {
  attributes?: {
    category_filter_settings?: SettingItem[];
    subcategory_filter_settings?: SettingItem[];
  };
};

export type CategoryFilterSettings = {
  /** Kategori facet'i bu sayfada listelenecek mi. */
  isCategoryFacetVisible: boolean;
  /** Alt kategori kırılımı (ağaç) gösterilecek mi. */
  showSubcategoryTree: boolean;
  /** Boş değilse yalnızca bu isimdeki ana kategoriler gösterilir. */
  allowedMainCategories: Set<string>;
};

export const EMPTY_CATEGORY_FILTER_SETTINGS: CategoryFilterSettings = {
  isCategoryFacetVisible: false,
  showSubcategoryTree: false,
  allowedMainCategories: new Set()
};

const normalizeUrl = (url = '') => url.replace(/\/$/, '');

export const normalizeCategoryName = (name = '') =>
  name.trim().toLocaleLowerCase('tr');

/** Widget kayıtları bazen `{ value: {...} }`, bazen doğrudan düz obje geliyor. */
const readSettingValue = (item?: SettingItem): SettingValue =>
  item?.value ?? item ?? {};

const isEnabled = (value: SettingValue) =>
  ENABLED_VALUES.includes(String(value.is_show));

const matchesPage = (value: SettingValue, pageUrl: string) =>
  normalizeUrl(value.page_url) === normalizeUrl(pageUrl);

const parseCategoryNames = (subcategory?: string) =>
  String(subcategory ?? '')
    .split(',')
    .map(normalizeCategoryName)
    .filter(Boolean);

/** Widget verisini, o sayfa için geçerli tek bir ayar setine indirger. */
export const resolveCategoryFilterSettings = (
  widget: CategoryFilterWidget | undefined,
  pageUrl: string
): CategoryFilterSettings => {
  const attributes = widget?.attributes;

  const activeSetting = (attributes?.category_filter_settings ?? [])
    .map(readSettingValue)
    .find((value) => isEnabled(value) && matchesPage(value, pageUrl));

  const allowedMainCategories = new Set(
    (attributes?.subcategory_filter_settings ?? [])
      .map(readSettingValue)
      .filter((value) => isEnabled(value) && matchesPage(value, pageUrl))
      .flatMap((value) => parseCategoryNames(value.subcategory))
  );

  return {
    isCategoryFacetVisible: Boolean(activeSetting),
    showSubcategoryTree:
      activeSetting?.is_subcategory_show === SUBCATEGORY_TREE_ENABLED_VALUE,
    allowedMainCategories
  };
};

export const useCategoryFilterSettings = (
  pageUrl: string
): CategoryFilterSettings => {
  const { data } = useGetWidgetQuery(CATEGORY_FILTER_WIDGET_SLUG);

  return useMemo(
    () => resolveCategoryFilterSettings(data as CategoryFilterWidget, pageUrl),
    [data, pageUrl]
  );
};

export const getChoiceDepth = (choice: FacetChoice) =>
  Number(choice.real_depth) || 0;

export const getTopDepth = (choices: FacetChoice[]) =>
  choices.length ? Math.min(...choices.map(getChoiceDepth)) : 0;

/**
 * Beyaz liste yalnızca ana kategori isimleriyle karşılaştırılır. Kırılımlar
 * (daha derin seviyeler) API çıktısında kendi ana kategorisini takip ettiği
 * için son görülen ana kategorinin durumunu devralır.
 *
 * `?category_ids=...` ile bir kategori seçildiğinde servis yalnızca o dalın
 * seçeneklerini döndürüyor; bu yanıtta hiçbir ana kategori bulunmadığı için
 * süzme uygulanmaz — aksi hâlde liste tamamen boşalır ve filtre kaybolur.
 */
const keepAllowedMainCategories = (
  choices: FacetChoice[],
  allowedMainCategories: Set<string>
) => {
  const topDepth = getTopDepth(choices);

  const isAllowedMain = (choice: FacetChoice) =>
    getChoiceDepth(choice) === topDepth &&
    allowedMainCategories.has(normalizeCategoryName(choice.label));

  if (!choices.some(isAllowedMain)) {
    return choices;
  }

  let isCurrentMainAllowed = false;

  return choices.filter((choice) => {
    if (getChoiceDepth(choice) === topDepth) {
      isCurrentMainAllowed = isAllowedMain(choice);
    }

    return isCurrentMainAllowed;
  });
};

const keepMainCategoriesOnly = (choices: FacetChoice[]) => {
  const topDepth = getTopDepth(choices);

  return choices.filter((choice) => getChoiceDepth(choice) === topDepth);
};

/** Kategori facet'inin seçeneklerini sayfa ayarlarına göre süzer. */
export const shapeCategoryChoices = (
  choices: FacetChoice[],
  settings: CategoryFilterSettings
): FacetChoice[] => {
  let result = choices;

  if (settings.allowedMainCategories.size > 0) {
    result = keepAllowedMainCategories(result, settings.allowedMainCategories);
  }

  if (!settings.showSubcategoryTree) {
    result = keepMainCategoriesOnly(result);
  }

  return result;
};

type FacetLike = {
  key: string;
  search_key?: string;
  data?: { choices?: FacetChoice[] };
};

/**
 * Servis, kategori sayfasının kendi kategorisini URL'de `category_ids` olmasa
 * bile `is_selected: true` döndürüyor. Aktif filtre chip'leri ve seçim sayacı bu
 * alandan beslendiği için sayfaya girer girmez var olmayan bir filtre görünüyor.
 *
 * Bu yüzden YALNIZCA `category_ids` facet'inin seçimi URL'deki parametreye göre
 * yeniden kuruluyor; diğer facet'ler hiç ellenmiyor.
 */
export const syncCategorySelection = <T extends FacetLike>(
  facets: T[],
  appliedParams: URLSearchParams
): T[] =>
  facets.map((facet) => {
    if (facet.key !== CATEGORY_FACET_KEY) {
      return facet;
    }

    const choices = facet.data?.choices ?? [];
    const selectedValues = appliedParams.getAll(
      facet.search_key ?? CATEGORY_FACET_KEY
    );

    const syncedChoices = choices.map((choice) => {
      const isSelected = selectedValues.includes(String(choice.value));

      return choice.is_selected === isSelected
        ? choice
        : { ...choice, is_selected: isSelected };
    });

    const hasChange = syncedChoices.some(
      (choice, index) => choice !== choices[index]
    );

    if (!hasChange) {
      return facet;
    }

    return {
      ...facet,
      data: { ...facet.data, choices: syncedChoices }
    };
  });

/**
 * Ham facet listesini store'a yazılacak hâle getirir: gizli facet'leri atar,
 * kategori facet'ini sayfa ayarlarına göre süzer.
 */
export const applyCategoryFilterSettings = <T extends FacetLike>(
  facets: T[],
  settings: CategoryFilterSettings
): T[] =>
  facets
    .filter((facet) => !HIDDEN_FACET_KEYS.includes(facet.key))
    .filter(
      (facet) =>
        facet.key !== CATEGORY_FACET_KEY || settings.isCategoryFacetVisible
    )
    .map((facet) => {
      if (facet.key !== CATEGORY_FACET_KEY) {
        return facet;
      }

      const choices = facet.data?.choices ?? [];
      const shapedChoices = shapeCategoryChoices(choices, settings);

      if (shapedChoices === choices) {
        return facet;
      }

      return {
        ...facet,
        data: { ...facet.data, choices: shapedChoices }
      };
    });
